import axios, { AxiosInstance, InternalAxiosRequestConfig, AxiosResponse, isAxiosError } from 'axios';
import { enqueueSnackbar } from 'notistack';

import { API_URL, APP_NAME, disableRequestTimeout, VERSION } from '../global/config';
import { AuthStorage } from '../localStorage/authStorage';

import {
  axiosErrorToHttpError,
  NotFoundError,
  UnauthorizedError,
  ForbiddenError,
  ProviderAuthError,
  UnsupportedAppVersionError,
  BadRequestError,
  HttpConnectionError
} from './apiErrors';

import {
  PROVIDER_AUTH_ERROR_MESSAGE,
  ForbiddenCodeEnum,
  ForbiddenResult,
  HEADER_APP_NAME,
  HEADER_APP_VERSION
} from './apiTypes.shim';
import { ourQueryClient } from './QueryProvider';
import { healthCheckResourceUri } from './resources/appConfigResource';

export const APP_VERSION_NOT_SUPPORTED = 'app-version-not-supported';
export const FORCE_APP_UPDATE_EVENT = 'force-app-update';
export const INVALID_AUTH = 'invalid-authentication';

/**
 * API returns specific code for certain 403 responses explaining reason for 403.
 * AccountCallTakingDisabled means the Account does not have call taking enabled, so UI should do something like
 * prompt/alert user they cannot take the call OR need to reach out to an Admin to resolve.
 */
function isAccountCallTakingForbiddenError(data: unknown): boolean {
  return (data as ForbiddenResult)?.code === ForbiddenCodeEnum.AccountCallTakingDisabled;
}

export const INVALID_PROVIDER_AUTH_EVENT = 'invalid-provider-authentication';

interface ProviderAuthErrorEventDetail {
  message: string;
  isProviderAuthErrorEvent: true;
}

export type ProviderAuthErrorEvent = CustomEvent<ProviderAuthErrorEventDetail>;

export function isProviderAuthErrorEvent(e: Event): e is ProviderAuthErrorEvent {
  return e instanceof CustomEvent && e.detail.isProviderAuthErrorEvent;
}

interface UnsupportedAppVersionErrorEventDetail {
  message: string;
  isUnsupportedAppVersionErrorEvent: true;
}

export type UnsupportedAppVersionErrorEvent = CustomEvent<UnsupportedAppVersionErrorEventDetail>;

export function isUnsupportedAppVersionErrorEvent(e: Event): e is UnsupportedAppVersionErrorEvent {
  return e instanceof CustomEvent && e.detail.isUnsupportedAppVersionErrorEvent;
}

const httpResponseErrorHandler =
  (apiClient: AxiosInstance) =>
  async (err: unknown): Promise<never | AxiosResponse | unknown> => {
    if (!isAxiosError(err)) return Promise.reject(err);

    const { disableSnackOnError, disableSnackOnNotFoundError } = err.config?.extraOptions || {};
    const httpError = axiosErrorToHttpError(err);

    if (httpError instanceof BadRequestError) return Promise.reject(httpError);

    if (httpError instanceof UnauthorizedError) {
      window.dispatchEvent(new Event(INVALID_AUTH));
      return Promise.reject(httpError);
    }

    if (httpError instanceof ForbiddenError) {
      if (isAccountCallTakingForbiddenError(httpError.axiosError.response?.data)) {
        window.dispatchEvent(new Event('invalid-call-taking-disabled'));
      } else if (!disableSnackOnError) {
        enqueueSnackbar('Access denied');
      }
      return Promise.reject(httpError);
    }

    if (httpError instanceof UnsupportedAppVersionError) {
      window.dispatchEvent(
        new CustomEvent<UnsupportedAppVersionErrorEventDetail>(APP_VERSION_NOT_SUPPORTED, {
          detail: {
            message: httpError.responseData.message || 'The app is out of date and must be updated.',
            isUnsupportedAppVersionErrorEvent: true
          }
        })
      );
      return Promise.reject(httpError);
    }

    if (httpError instanceof NotFoundError) {
      if (!disableSnackOnError && !disableSnackOnNotFoundError) enqueueSnackbar('Resource not found');
      return Promise.reject(httpError);
    }

    if (httpError instanceof ProviderAuthError) {
      const event = new CustomEvent<ProviderAuthErrorEventDetail>(INVALID_PROVIDER_AUTH_EVENT, {
        detail: {
          message: httpError.responseData.nonFieldErrors[0] || PROVIDER_AUTH_ERROR_MESSAGE,
          isProviderAuthErrorEvent: true
        }
      });
      window.dispatchEvent(event);
      return Promise.reject(httpError);
    }

    // Once we exhaust handling "expected", retry requests resulting in unexpected error
    if (err.config?.extraOptions?.retry && err.config.extraOptions.retry > 0) {
      return apiClient.request({ ...err.config, extraOptions: { retry: err.config.extraOptions.retry - 1 } });
    }

    // Once we exhaust handling "expected" errors and retries, pop general snack error
    if (httpError instanceof HttpConnectionError && !disableSnackOnError) {
      enqueueSnackbar('Network Connection Error');
    } else if (!disableSnackOnError) {
      enqueueSnackbar('Unexpected Error. Our development team has been notified. Please try again.');
    }

    return Promise.reject(httpError);
  };

function authHeaderInjector(requestConfig: InternalAxiosRequestConfig) {
  const token = AuthStorage.authToken.get() || '';
  requestConfig.headers['Authorization'] = token;
  return requestConfig;
}

function appIdentityHeaderInjector(requestConfig: InternalAxiosRequestConfig) {
  requestConfig.headers[HEADER_APP_NAME] = APP_NAME;
  requestConfig.headers[HEADER_APP_VERSION] = VERSION;
  return requestConfig;
}

function isFailoverInterceptor(response: AxiosResponse) {
  const isFailoverValue = response.headers && response.headers['is-failover'];
  if (typeof isFailoverValue === 'string') {
    ourQueryClient.setQueryData([healthCheckResourceUri], (oldData: object) => ({
      ...oldData,
      isFailover: isFailoverValue === 'true'
    }));
  }
  return response;
}

function createApiClient(baseURL: string): AxiosInstance {
  const apiClient = axios.create({ baseURL, timeout: disableRequestTimeout ? 0 : 20000 });

  // Add the authorization token as header to each request
  apiClient.interceptors.request.use(authHeaderInjector);
  // Add app name and version headers to each request.
  apiClient.interceptors.request.use(appIdentityHeaderInjector);
  // Add isFailover interceptor to each response
  apiClient.interceptors.response.use(isFailoverInterceptor);
  // Add any special error interceptors:
  apiClient.interceptors.response.use(undefined, httpResponseErrorHandler(apiClient));

  return apiClient;
}

export const API = createApiClient(API_URL);
export const ServiceTitanAPI = createApiClient(`${API_URL}/st`);
export const PoolBrainAPI = createApiClient(`${API_URL}/pool-brain`);
